import { useEffect, useState } from "react";
import { api } from "./api";
import type { MeResponse } from "@shared/types";

let cache: MeResponse | null = null;
let inflight: Promise<MeResponse> | null = null;

// Module-level pub/sub: `invalidateMe()` can be called from anywhere (a sheet
// that doesn't hold its own `useMe()` instance, e.g. ResetPassword/VerifyEmail)
// but every *mounted* `useMe()` consumer needs to know to refetch too — the
// module cache being null doesn't by itself cause a re-render. Each hook
// instance subscribes a listener that bumps its own `tick`; invalidateMe()
// notifies all of them.
const listeners = new Set<() => void>();

export function useMe(): { me: MeResponse | null; refresh: () => void } {
  const [me, setMe] = useState<MeResponse | null>(cache);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // By the time this effect (re)runs, invalidateMe() has already nulled
    // `cache`/`inflight` synchronously (tick changes are how we get here on
    // re-runs), so a plain cache check is enough — no `tick > 0` special case.
    if (cache) { setMe(cache); return; }
    inflight ??= api.me();
    inflight.then((m) => { cache = m; if (!cancelled) setMe(m); }).catch(() => {});
    return () => { cancelled = true; };
  }, [tick]);

  useEffect(() => {
    const onInvalidate = () => setTick((t) => t + 1);
    listeners.add(onInvalidate);
    return () => { listeners.delete(onInvalidate); };
  }, []);

  // Route through invalidateMe() so there's exactly one "clear + refetch
  // everywhere" code path, whether triggered by this hook or an external caller.
  return { me, refresh: invalidateMe };
}

/** Invalidate the cached MeResponse and notify every mounted useMe() consumer to refetch. */
export function invalidateMe(): void {
  cache = null;
  inflight = null;
  for (const l of listeners) l();
}
